import type { ReactNode } from 'react'
import { InlineHint } from './InlineHint'

// ---------------------------------------------------------------------------
// Context-aware tips — show smart hints based on first-time user actions
// ---------------------------------------------------------------------------

type TipContext = 'first-sign' | 'first-template' | 'first-negotiation' | 'first-archive'

const TIP_FLAG_PREFIX = 'kobsign-ctx-'
const HINT_PREFIX = 'kobsign-hint-'

/** Check if user has already completed an action (stored in localStorage) */
function hasCompletedAction(context: TipContext): boolean {
  return localStorage.getItem(`${TIP_FLAG_PREFIX}${context}`) === 'done'
}

/** Mark an action as completed so tip won't show again */
export function markActionCompleted(context: TipContext): void {
  localStorage.setItem(`${TIP_FLAG_PREFIX}${context}`, 'done')
}

interface ContextualTipProps {
  /** Which context triggers this tip */
  context: TipContext
  /** i18n-translated tip text */
  children: ReactNode
}

/**
 * Context-aware tip that shows only for first-time actions.
 * Combines localStorage tracking (has user done this before?) with
 * the InlineHint dismiss system (user can also manually dismiss).
 *
 * Usage:
 *   <ContextualTip context="first-sign">
 *     {t('tips.firstSign')}
 *   </ContextualTip>
 */
export function ContextualTip({ context, children }: ContextualTipProps) {
  // Compute visibility once at mount — no useEffect needed
  const actionDone = hasCompletedAction(context)
  const hintDismissed = localStorage.getItem(`${HINT_PREFIX}ctx-${context}`) === 'dismissed'

  if (actionDone || hintDismissed) return null

  return (
    <InlineHint hintKey={`ctx-${context}`} pulse>
      {children}
    </InlineHint>
  )
}
